/**
 * Super Admin — Marketplace : gestion du prix unique par pack sectoriel.
 * Le prix (EUR) est lu par le backend au moment de l'achat → toute modification
 * s'applique immédiatement à la Marketplace client et au checkout Stripe.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Save, Package, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PackRow({ pack, auth, onSaved }) {
    const [price, setPrice] = useState(String(pack.price_eur ?? ''));
    const [active, setActive] = useState(!!pack.is_active);
    const [saving, setSaving] = useState(false);
    const dirty = Number(price) !== Number(pack.price_eur) || active !== !!pack.is_active;

    const save = async () => {
        const p = Number(price);
        if (!Number.isFinite(p) || p < 0) { toast.error('Prix invalide'); return; }
        setSaving(true);
        try {
            const r = await axios.patch(`${API}/super-admin/marketplace/packs/${pack.id}`,
                { price_eur: p, is_active: active }, auth);
            toast.success(`${pack.name} : ${r.data.pack.price_eur} € enregistré`);
            onSaved(r.data.pack);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Sauvegarde échouée');
        } finally { setSaving(false); }
    };

    return (
        <tr className="border-b border-border/60 hover:bg-surface-muted/40" data-testid={`sa-pack-row-${pack.slug}`}>
            <td className="px-4 py-3">
                <div className="flex items-center gap-2.5">
                    <span className="text-xl">{pack.cover_emoji || '📂'}</span>
                    <div>
                        <div className="font-medium text-sm">{pack.name}</div>
                        <div className="text-[11px] text-muted-foreground">{pack.sector} · {pack.templates_count} modèles</div>
                    </div>
                </div>
            </td>
            <td className="px-4 py-3">
                <div className="flex items-center gap-1.5">
                    <Input type="number" min={0} step="0.01" value={price}
                        onChange={(e) => setPrice(e.target.value)}
                        className="h-9 w-28 text-sm text-right"
                        data-testid={`sa-pack-price-${pack.slug}`} />
                    <span className="text-sm text-muted-foreground">€</span>
                </div>
            </td>
            <td className="px-4 py-3 text-center">
                <Switch checked={active} onCheckedChange={setActive} data-testid={`sa-pack-active-${pack.slug}`} />
            </td>
            <td className="px-4 py-3 text-right">
                <Button size="sm" onClick={save} disabled={saving || !dirty}
                    className="bg-primary hover:bg-primary-hover"
                    data-testid={`sa-pack-save-${pack.slug}`}>
                    {saving ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <Save size={13} className="mr-1.5" />}
                    Enregistrer
                </Button>
            </td>
        </tr>
    );
}

export default function MarketplaceAdmin({ auth }) {
    const [packs, setPacks] = useState(null);
    const [stats, setStats] = useState(null);

    const load = async () => {
        try {
            const [rp, rs] = await Promise.all([
                axios.get(`${API}/super-admin/marketplace/packs`, auth),
                axios.get(`${API}/super-admin/marketplace/stats`, auth),
            ]);
            setPacks(rp.data.packs || []);
            setStats(rs.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Chargement échoué');
            setPacks([]);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const onSaved = (updated) => setPacks(ps => ps.map(p => p.id === updated.id ? updated : p));

    if (!packs) return <div className="flex items-center justify-center py-20" data-testid="sa-marketplace-loading"><Loader2 className="animate-spin" /></div>;

    return (
        <div className="space-y-5" data-testid="sa-marketplace-admin">
            {stats && (
                <div className="space-y-4" data-testid="sa-marketplace-stats">
                    <div className="grid grid-cols-3 gap-4">
                        <div className="rounded-xl border border-border bg-white p-4">
                            <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Revenu packs (EUR)</div>
                            <div className="font-heading text-2xl text-primary" data-testid="sa-mp-revenue">{stats.total_revenue_eur.toLocaleString('fr-FR')} €</div>
                        </div>
                        <div className="rounded-xl border border-border bg-white p-4">
                            <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Ventes totales</div>
                            <div className="font-heading text-2xl" data-testid="sa-mp-sales">{stats.total_sales}</div>
                        </div>
                        <div className="rounded-xl border border-border bg-white p-4">
                            <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Abonnements actifs</div>
                            <div className="font-heading text-2xl text-secondary" data-testid="sa-mp-active">{stats.total_active}</div>
                        </div>
                    </div>
                    {stats.packs.length > 0 ? (
                        <div className="rounded-xl border border-border bg-white overflow-hidden">
                            <div className="px-4 py-3 text-sm font-medium border-b border-border/60 flex items-center gap-2">
                                <TrendingUp size={15} className="text-secondary" /> Packs les plus vendus
                            </div>
                            <table className="w-full text-left text-sm">
                                <thead className="text-[11px] uppercase tracking-wider text-muted-foreground bg-surface-muted/60">
                                    <tr>
                                        <th className="px-4 py-2">#</th>
                                        <th className="px-4 py-2">Pack</th>
                                        <th className="px-4 py-2 text-right">Ventes</th>
                                        <th className="px-4 py-2 text-right">Actifs</th>
                                        <th className="px-4 py-2 text-right">Revenu</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stats.packs.slice(0, 8).map((p, i) => (
                                        <tr key={p.slug || i} className="border-b border-border/40 last:border-0" data-testid={`sa-mp-top-${p.slug}`}>
                                            <td className="px-4 py-2.5 text-muted-foreground">{i + 1}</td>
                                            <td className="px-4 py-2.5">{p.cover_emoji} {p.name}</td>
                                            <td className="px-4 py-2.5 text-right">{p.sales}</td>
                                            <td className="px-4 py-2.5 text-right">{p.active}</td>
                                            <td className="px-4 py-2.5 text-right font-medium text-primary">{p.revenue_eur.toLocaleString('fr-FR')} €</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="rounded-xl border border-dashed border-border bg-surface-muted/30 px-4 py-5 text-sm text-muted-foreground" data-testid="sa-mp-empty">
                            Aucune vente de pack enregistrée pour le moment — les statistiques apparaîtront dès la première commande.
                        </div>
                    )}
                </div>
            )}
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <Package size={16} className="text-primary" />
                <span>Prix unique par pack (EUR), appliqué immédiatement à la Marketplace client et au paiement Stripe. Désactivez un pack pour le masquer du catalogue.</span>
            </div>
            <div className="rounded-xl border border-border overflow-hidden bg-white">
                <table className="w-full text-left">
                    <thead className="bg-surface-muted/60 text-[11px] uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="px-4 py-3">Pack ({packs.length})</th>
                            <th className="px-4 py-3">Prix unique</th>
                            <th className="px-4 py-3 text-center">Actif</th>
                            <th className="px-4 py-3"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {packs.map(p => <PackRow key={p.id} pack={p} auth={auth} onSaved={onSaved} />)}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
